import React, { useReducer, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSize } from 'ahooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { hasCategory } from 'shared/utils/categories';
import { GRADE, NPS, CSI, FIND } from 'shared/constants/questionCategories';
import { QUESTION_TYPES } from 'shared/constants/survey';

import ChartSubtitles from 'components/Charts/ChartSubtitles';
import BarChart from 'components/Charts/BarChart';
import PieChart from 'components/Charts/PieChart';
import LineChart from 'components/Charts/LineChart';
import StackedBarChart from 'components/Charts/StackedBarChart';
import DataTable from 'components/Charts/DataTable';

import { Checkbox } from 'antd';
import globalMessages from 'translations/messages';
import useFormatMessage from 'hooks/useFormatMessage';
import useDownloadAsExcel from 'hooks/useDownloadAsExcel';
import { getExportData } from 'components/Charts/utils';
import messages from './messages';

const { CHECKBOX, ZIP } = QUESTION_TYPES;

const getValues = (segment, $t) => (v) => ({
  ...v,
  id: v.id || v.name,
  name: ($t && messages[v.name] && $t(messages[v.name])) || v.name,
  segmentId: segment.segmentId,
});

const getSubTitles = (question, $t) => {
  const subTitles = [];

  if (question.translateTitle && messages[`${question.title}-subtitle`]) {
    subTitles.push($t(messages[`${question.title}-subtitle`]));
  }
  if (question.type === CHECKBOX) {
    subTitles.push($t(messages.multipleSelectionQuestion));
  } if (question.type === ZIP) {
    subTitles.push($t(messages.zipQuestion));
  }

  return subTitles;
};

const getChartType = (isScoreGraph, question) => {
  if (isScoreGraph) {
    if (question.title === 'findScore') { return 'line'; }
    if (question.title === 'npsScore') { return 'line'; }
    if (question.title === 'csiScore') { return 'bar'; }
    if (question.title === 'grades') { return 'bar'; }
  }
  if (question.type === QUESTION_TYPES.FIND || hasCategory(question.categories, [FIND])) { return 'pie'; }
  if (question.type === QUESTION_TYPES.GRADE || hasCategory(question.categories, [GRADE])) { return 'pie'; }
  if (question.type === QUESTION_TYPES.NPS || hasCategory(question.categories, [NPS])) { return 'pie'; }
  if (hasCategory(question.categories, [CSI])) { return 'stacked'; }
  return 'bar';
};

const getScoreType = (question) => {
  if (question.type === QUESTION_TYPES.FIND || hasCategory(question.categories || question.scoreCategories, [FIND])) { return 'find'; }
  if (question.type === QUESTION_TYPES.GRADE || hasCategory(question.categories || question.scoreCategories, [GRADE])) { return 'grade'; }
  if (question.type === QUESTION_TYPES.NPS || hasCategory(question.categories || question.scoreCategories, [NPS])) { return 'nps'; }
  return 'score';
};

const isSwitchedAxis = (isScoreGraph, question) => !!([QUESTION_TYPES.GRADE, QUESTION_TYPES.NPS, QUESTION_TYPES.FIND].includes(question.type) || hasCategory(question.categories, [GRADE, NPS, FIND]) || isScoreGraph);

// Convert optionids to labels
const disabledLegendsToSelected = (disabledLegends, question) => disabledLegends.reduce((obj, disabledLegend) => {
  const label = question.questionOptions?.find(o => o.optionIds.includes(disabledLegend))?.title || disabledLegend;
  return { ...obj, [label]: false };
}, {});

const resolveChartSettings = ({ selected = {}, ...rest }, question) => ({
  ...rest,
  disabledLegends: Object.keys(selected).filter(k => !selected[k]).map(label => question.questionOptions?.find(o => o.title === label)?.id || label),
});

const initChartSettings = (question, isScoreGraph, chartSettings) => {
  const {
    type,
    switchedAxis,
    displayMode,
    disabledLegends = [],
    displayGradingScale,
  } = chartSettings;

  return {
    ...chartSettings,
    type: type || getChartType(isScoreGraph, question),
    switchedAxis: switchedAxis != null ? switchedAxis : isSwitchedAxis(isScoreGraph, question),
    displayMode: displayMode || 'percentage',
    selected: disabledLegendsToSelected(disabledLegends, question),
    displayGradingScale: displayGradingScale != null ? displayGradingScale : true,
  };
};

const chartSettingsReducer = (state, action = {}) => ({ ...state, ...action });

const OptionsResult = (props) => {
  const {
    title,
    subTitles = [],
    question,
    periods,
    onChartSettingsChanged,
    isPrint,
    isReport,
    compare,
    onEdit,
    comparablePeriods,
  } = props;

  const isScoreGraph = question.type === 'SCORE';

  const containerRef = useRef();
  const size = useSize(containerRef);
  const $t = useFormatMessage();
  const download = useDownloadAsExcel();
  const { locale } = useIntl();
  const [chartSettings, dispatch] = useReducer(chartSettingsReducer, initChartSettings(question, isScoreGraph, props.chartSettings));

  useEffect(() => {
    if (onChartSettingsChanged) {
      onChartSettingsChanged(resolveChartSettings(chartSettings, question));
    }
  }, [chartSettings]);

  const segments = useMemo(() => question.segments.map(s => {
    let values = s.values?.map(getValues(s, question.translateNames && $t));
    let displayCount;
    let name;

    if (question.type === 'FUNNEL') {
      if (!chartSettings.showPreviousStep) {
        values = values.filter(v => v.type !== 'PREVIOUS_STEP');
        displayCount = s.uniqueCountForStep;
      }
      if (!chartSettings.showDropOff) {
        values = values.filter(v => v.type !== 'DROP_OFF');
      }
    }

    if (s.isGlobal) {
      name = $t(globalMessages.globalBenchmark);
    } else {
      name = s.name;
    }

    return {
      ...s,
      name,
      values,
      displayCount,
    };
  }), [question, chartSettings]);

  const chartSubTitles = getSubTitles(question, $t);
  const scoreType = getScoreType(question);

  const addToReport = props.addToReport && (() => {
    props.addToReport({
      chartSettings: resolveChartSettings(chartSettings, question),
    });
  });

  const onSwitchChartType = (type) => {
    let { displayMode } = chartSettings;
    if (type === 'line') {
      if (segments.some(s => s.score)) {
        displayMode = 'score';
      }
    } else if (displayMode === 'score') {
      displayMode = 'percentage';
    }

    dispatch({ type, displayMode });
  };

  const onSelectChartSegment = useCallback((data) => {
    props.onSelectChartSegment?.({ data, question });
  }, [props.onSelectChartSegment]);

  const onDownload = useCallback(() => {
    const { columns, rows } = getExportData({ segments, displayMode: chartSettings.displayMode, isScoreGraph, formatMessage: $t, locale });
    try {
      download({
        fileName: title,
        sheetTitle: 'Results',
        sheetData: rows,
        sheetHeader: columns,
      });
    } catch (e) {
      console.error(e);
    }
  }, [download, segments, chartSettings, title, isScoreGraph]);

  let ChartComponent;

  switch (chartSettings.type) {
    case 'bar':
      ChartComponent = BarChart;
      break;
    case 'pie':
      ChartComponent = PieChart;
      break;
    case 'area':
    case 'line':
      ChartComponent = LineChart;
      break;
    case 'stacked':
      ChartComponent = StackedBarChart;
      break;
    case 'table':
      ChartComponent = DataTable;
      break;
    default:
  }

  const toolbox = {
    switchChartType: onSwitchChartType,
    switchDisplayMode: (displayMode) => dispatch({ displayMode }),
    showPercentage: !isScoreGraph,
    compare,
    addToReport,
    onEdit,
    chartTypes: comparablePeriods ? ['bar', 'pie', 'line', 'area', 'stacked'] : ['bar', 'pie', 'stacked'],
    onChange: dispatch,
    ...props.toolbox,
    onDownload,
  };

  if (question.type === 'FUNNEL') {
    toolbox.chartTypes = ['pie', 'stacked'];
    toolbox.left = (
      <>
        <Checkbox checked={chartSettings.showPreviousStep} onChange={() => dispatch({ showPreviousStep: !chartSettings.showPreviousStep })}><FormattedMessage {...messages.showPreviousStep} /></Checkbox>
        <Checkbox checked={chartSettings.showDropOff} onChange={() => dispatch({ showDropOff: !chartSettings.showDropOff })}><FormattedMessage {...messages.showDropOff} /></Checkbox>
      </>
    );
  }

  return (
    <div className="options-result" ref={containerRef}>
      <ChartSubtitles subTitles={[...chartSubTitles, ...subTitles]} />
      <ChartComponent
        title={title}
        scoreType={scoreType}
        question={question}
        isScoreGraph={isScoreGraph}
        isMultipleChoice={question.type === 'CHECKBOX'}
        segments={segments}
        periods={periods}
        chartSettings={chartSettings}
        onChartSettingsChange={dispatch}
        isReport={isReport}
        isPrint={isPrint}
        width={size?.width}
        toolbox={toolbox}
        onSelectChartSegment={props.onSelectChartSegment && question.filterType && onSelectChartSegment}
      />
    </div>
  );
};

export default OptionsResult;
